import { styled } from '@compiled/react';
import React, { Fragment, useMemo } from 'react';

import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { GroupedPageButtonsCommon } from '@confluence/content-header/entry-points/GroupedPageButtonsCommon.js';
import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import type { TargetContentType } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { CURRENT_STATUS } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import { DELETE_PAGE_LINK } from '@confluence/content-tools/entry-points/constants.ts';
import { CommentButtonWrapper } from '@confluence/comment-button/entry-points/CommentButtonWrapper.ts';
import { INVITE_TO_EDIT_KEY } from '@confluence/web-item/entry-points/webItemKeys.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { VIEW_PAGE_SHARE_BUTTON_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { PAGE_BUTTONS } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import type { WebItemLocationChildrenFnWithLoading } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { ShareButtonPlaceholder } from '@confluence/share/entry-points/ShareButtonPlaceholder.ts';
import { ShareAndRestrictButton } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButton.tsx';
import { SHARE_BTN_STYLE } from '@confluence/web-item-list/entry-points/WebItemList.ts';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';
import { fg } from '@confluence/feature-gating';
import { LoomExternalOpenButton } from '@confluence/loom-external-open/entry-points/LoomExternalOpenButton.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/child-client';

import { StickyHeaderContainer } from './StickyHeaderContainer.tsx';

type HeadingContainerProps = {
	children: React.ReactNode;
	contentId: string;
	hasInlineComments: boolean;
	hasStickyHeader: boolean;
	spaceKey?: string;
	showDelete: boolean;
	showEdit: boolean;
	showInlineCommentButton: boolean;
	showShare: boolean;
	includeNonLicensedUsersToShare: boolean;
	hasCoverPicture?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	alignItems: 'center',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledGroupedPageButtonContainer = styled.div<{
	isEmbeddedConfluence?: boolean;
	hasCoverPicture?: boolean;
	isObjectHeaderEnabled?: boolean;
}>({
	display: 'flex',
	paddingTop: token('space.050') /* Account for title h1's padding */,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-testid="gpb-common"] > div': {
		minHeight: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ isEmbeddedConfluence, hasCoverPicture, isObjectHeaderEnabled }) => {
		if (isObjectHeaderEnabled) {
			return '0';
		}
		return isEmbeddedConfluence && hasCoverPicture ? token('space.negative.300') : 'initial';
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContentTitleWithActions = styled.div<{
	isEmbeddedConfluence?: boolean;
	isTitleCenterAligned?: boolean;
}>({
	display: 'flex',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isEmbeddedConfluence }) => (isEmbeddedConfluence ? 'column' : 'initial'),
	boxSizing: 'border-box',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ isEmbeddedConfluence, isTitleCenterAligned }) =>
		isEmbeddedConfluence && !isTitleCenterAligned ? 'left' : 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShareButtonPlaceholderSpacer = styled.span({
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightAlignWrapper = styled.div<{ isEmbeddedConfluence?: boolean; hasCoverPicture?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isEmbeddedConfluence, hasCoverPicture }) =>
		isEmbeddedConfluence
			? {
					width: '100%',
					display: 'flex',
					justifyContent: 'flex-end',
					marginTop: hasCoverPicture ? token('space.300') : 'initial',
					// contains at least one button in gpb-common
					'&:has(svg, button, a)': {
						marginBottom: token('space.300'),
					},
					// overwrite to add more margin in place of gpb-common
					'&:not(:has(svg, button, a))': {
						marginTop: hasCoverPicture ? token('space.600') : 'initial',
					},
				}
			: {},
);

export const HeaderContainer = ({
	children,
	contentId,
	hasInlineComments,
	hasStickyHeader,
	spaceKey,
	showDelete,
	showEdit,
	showInlineCommentButton,
	showShare,
	hasCoverPicture,
}: HeadingContainerProps) => {
	const { loomExternalOpenRoute } = useEmbeddedViewPageFeatures();

	const allowedContentToolsItems = useMemo(
		() => (showDelete ? [DELETE_PAGE_LINK] : []),
		[showDelete],
	);

	const TitleWrapper = hasStickyHeader ? StickyHeaderContainer : Fragment;

	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
	const isMobileApp = isMobileApp_DO_NOT_USE();
	const isEmbeddedConfluenceOrMobileApp = isEmbeddedConfluence || isMobileApp;
	const isNativeEmbed = maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
	const shouldHideCommentsButtonForNativeEmbedUiUpdates =
		isNativeEmbed && expValEquals('cc-maui-experiment', 'isEnabled', true);

	const { titleContentProperties } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});

	const [contentType] = useContentType();
	const canUpdateContent = useCanPerformContentOperation({
		contentId,
		contentType: (contentType ?? 'page') as TargetContentType,
		operation: OperationTypes.UPDATE,
		status: CURRENT_STATUS,
	});
	const hasEditPermission = canUpdateContent ?? false;

	const isTitleCenterAligned = !!(
		titleContentProperties?.titleLayoutAlignment === TitleAlignmentType.CENTER
	);

	const commentResidesInPreviewPanel = fg('confluence_previewpanel_enable_comments_panel')
		? isInPreviewPanel()
		: false;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isCommentButtonSSRd = Boolean(window.__SSR_RENDERED__ || process.env.REACT_SSR);

	return (
		<TitleWrapper>
			<StyledContentTitleWithActions
				isEmbeddedConfluence={isEmbeddedConfluenceOrMobileApp}
				isTitleCenterAligned={isTitleCenterAligned}
			>
				{!isEmbeddedConfluenceOrMobileApp && children}
				<RightAlignWrapper
					isEmbeddedConfluence={isEmbeddedConfluenceOrMobileApp}
					hasCoverPicture={hasCoverPicture}
				>
					<StyledGroupedPageButtonContainer
						isEmbeddedConfluence={isEmbeddedConfluenceOrMobileApp}
						hasCoverPicture={hasCoverPicture}
						isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
					>
						{!fg('cc_page_experiences_new_editor_byline') &&
							hasEditPermission &&
							(expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
								<EditorContentState
									contentId={contentId}
									contentType={(contentType as 'page' | 'blogpost' | null) ?? 'page'}
									isViewMode
									userOffline={false}
								/>
							) : (
								<RendererContentState
									contentId={contentId}
									isDraft={false}
									hasSeparator={showEdit || allowedContentToolsItems.length > 0}
								/>
							))}
						{
							// @ts-ignore - Type '{ children: (false | Element)[]; allowedContentToolsItems: string[]; contentId: string; showEdit: boolean; spaceKey: string | undefined; }' is not assignable to type 'IntrinsicAttributes & object'.
							// Using `ts-ignore` and not `ts-expect-error` here as TypeScript will fail on this line without `ts-ignore` but with `ts-expect-error`
							// This error was introduced after upgrading to TypeScript 5.
							<GroupedPageButtonsCommon
								allowedContentToolsItems={allowedContentToolsItems}
								contentId={contentId}
								showEdit={showEdit}
								spaceKey={spaceKey}
							>
								{hasInlineComments &&
									showInlineCommentButton &&
									!commentResidesInPreviewPanel &&
									!shouldHideCommentsButtonForNativeEmbedUiUpdates && (
										<CommentButtonWrapper
											key={contentId}
											contentId={contentId}
											isCommentButtonSSRd={isCommentButtonSSRd}
										/>
									)}
								{showShare && (
									<StyledContainer>
										<ExperienceStart
											key="view-page-share-button-start"
											id={contentId}
											name={VIEW_PAGE_SHARE_BUTTON_EXPERIENCE}
										/>
										<WebItemLocation
											key="share-button"
											tagName="span"
											contentId={contentId}
											allowedWebItems={[INVITE_TO_EDIT_KEY]}
											location={PAGE_BUTTONS}
											renderWhenLoading
										>
											{
												(({ loading, webItems }) => {
													if (loading) {
														return (
															<ShareButtonPlaceholderSpacer>
																<ShareButtonPlaceholder />
															</ShareButtonPlaceholderSpacer>
														);
													} else if (webItems.find((t) => t.completeKey === INVITE_TO_EDIT_KEY)) {
														return (
															<span
																// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
																style={SHARE_BTN_STYLE}
																key="fabric-share"
															>
																<ShareAndRestrictButton
																	contentId={contentId}
																	contentType="page"
																	shareeAction="view"
																	source="embedded-confluence-header"
																/>
															</span>
														);
													}
													return null;
												}) as WebItemLocationChildrenFnWithLoading
											}
										</WebItemLocation>
									</StyledContainer>
								)}
								{loomExternalOpenRoute && fg('mr2-meeting-recording-notes-confluence') && (
									<Box paddingInlineStart="space.075">
										<LoomExternalOpenButton />
									</Box>
								)}
							</GroupedPageButtonsCommon>
						}
					</StyledGroupedPageButtonContainer>
				</RightAlignWrapper>
				{isEmbeddedConfluenceOrMobileApp && children}
			</StyledContentTitleWithActions>
		</TitleWrapper>
	);
};
